import type { ReactNode } from 'react';
// import { useGetPlatformSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import React, { createContext, useContext, useMemo } from 'react';

/**
 * Platform entity returned from the GET /platform endpoint
 */
export type PlatformEntity = {
  workspaceId: string;
  displayName: string;
  created: string;
  modified?: string;
};

type PlatformEntityContextValue = {
  platformEntity: PlatformEntity | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

const defaultContext: PlatformEntityContextValue = {
  platformEntity: null,
  isLoading: false,
  isError: false,
  error: null,
  refetch: () => {},
};

const PlatformEntityContext =
  createContext<PlatformEntityContextValue>(defaultContext);

type Props = {
  children: ReactNode;
};

/**
 * Provider that fetches and provides the platform entity from the Payments API.
 * This should be rendered within the PaymentsApiAuthProvider context.
 */
export const PlatformEntityProvider = ({ children }: Props) => {
  // const { data, isLoading, isError, error, refetch } = useGetPlatformSuspense();

  const contextValue: PlatformEntityContextValue = useMemo(
    () => ({
      // eslint-disable-next-line
      platformEntity: null,
      isLoading: false,
      isError: false,
      error: null,
      refetch: () => {},
    }),
    []
  );

  return (
    <PlatformEntityContext.Provider value={contextValue}>
      {children}
    </PlatformEntityContext.Provider>
  );
};

/**
 * Hook to access the platform entity context.
 * Must be used within a PlatformEntityProvider.
 */
export const usePlatformEntity = (): PlatformEntityContextValue => {
  const context = useContext(PlatformEntityContext);
  return context;
};
